import { appApi, idTag, orgTag } from '@/app/redux/appApi';
import {
  CreateCustomLevelInput,
  CustomLevel,
  UpdateCustomLevelInput,
} from '@/common/types/customLevel';
import api from '@/db/apiClient';

const customLevelsApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getCustomLevelsForOrganisation: build.query<CustomLevel[], string>({
      query: (orgId) => api.asEducator.getCustomLevelsForOrganisation(orgId),
      providesTags: (result, _err, orgId) =>
        result
          ? [
              orgTag('CustomLevels', orgId),
              ...result.map((cl) => idTag('CustomLevels', cl.id)),
            ]
          : [orgTag('CustomLevels', orgId)],
    }),

    createCustomLevel: build.mutation<CustomLevel, CreateCustomLevelInput>({
      query: (data) => api.asEducator.createCustomLevel(data),
      invalidatesTags: (_res, _err, data) => [
        orgTag('CustomLevels', data.organisationId),
      ],
    }),

    updateCustomLevel: build.mutation<
      null,
      { orgId: string; id: string; updates: UpdateCustomLevelInput }
    >({
      query: ({ id, updates }) => api.asEducator.updateCustomLevel(id, updates),
      onQueryStarted: async (
        { orgId, id, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          customLevelsApi.util.updateQueryData(
            'getCustomLevelsForOrganisation',
            orgId,
            (draft) => {
              const cl = draft.find((x) => x.id === id);
              if (cl) Object.assign(cl, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteCustomLevel: build.mutation<null, { orgId: string; id: string }>({
      query: ({ id }) => api.asEducator.deleteCustomLevel(id),
      onQueryStarted: async ({ orgId, id }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          customLevelsApi.util.updateQueryData(
            'getCustomLevelsForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.findIndex((cl) => cl.id === id);
              if (idx !== -1) draft.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default customLevelsApi;
export const {
  useGetCustomLevelsForOrganisationQuery,
  useCreateCustomLevelMutation,
  useUpdateCustomLevelMutation,
  useDeleteCustomLevelMutation,
} = customLevelsApi;
